import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { isTrackerVisible, now, isAfter } from 'shared-components';
import { ApplicationState } from '../../../store';
import { selectIntakeAfterCutoff } from '../../../store/selectors';
import {
  updateArrivalStatus,
  downloadAttachments,
  getAuditLog,
  getStudentRecord,
  markMessagesRead,
  markMessageUnread,
  saveMessage,
  updateStatus,
} from '../../../store/student/actions';

import {
  AGENT_CONFLICT_SUMMARY_KEYS,
  CoreStatusExtended,
  EventInfoStatus,
  ICalendlyEvent,
  IMessage,
  ISummary,
  RecordedInterviewSystemStatus,
  SectionStatusType,
  TravelStatusEnum,
  InfoState,
} from 'api-paths-cas';
import DashboardPage, { IDispatchProps, IStateProps } from '.';
import { studentsUnpinTags, studentsPinTags } from '../../../store/dashboard/actions';
import { updateVisaStatus } from '../../../store/student/core/actions';
import { Status, VisaStatus } from '../../../store/student/core/types';
import { preCasFlags } from '../../../store/student/preCasInterview/reducer';
import {
  isChecklistRisk,
  isDocsRisk,
  isFinDocRisk,
  isInactiveChecklistRisk,
  isInactiveDocsRisk,
  isInactiveFinDocsRisk,
  isInactivePreCasRisk,
  isPreCasRisk,
} from '../../../store/student/risks/reducer';
import { IArrivalStatus, IDate } from '../../../types';
import { filterMessagesByType } from '../../../utilities/filterMessages';
import { getDisplayName } from '../../../utilities/formatting';
import { getDisplayCalendlyInterviewsAllowed, getDisplayRecordedInterviewAllowed } from '../../../utilities/helpers';
import { visaPrepFlags } from '../../../utilities/pages';
import { P, hasPerm, isAgentOrAgencyRole, isEnrolyStaff, isUniStaffRole } from '../../../utilities/roles/permissions';
import { SectionInfoState } from '../../molecules/studentLeftNav/types';
import { all } from './info/fieldData';
import { UserRole } from '@/store/auth/types';
// import { navigateToUrl } from 'single-spa';

const hasAgentConflicts = (state: ApplicationState): boolean => {
  const agentDetailsEnabled = state.student.config?.agentDetailsEnabled !== false;
  if (!agentDetailsEnabled) return false;
  if (!selectIntakeAfterCutoff(state)) return false;
  const conflicts = state.student.summary?.conflicts;
  if (!conflicts) return false;
  return AGENT_CONFLICT_SUMMARY_KEYS.some(key => !!conflicts[key]);
};

const completedStates = [
  InfoState.Filled,
  InfoState.Approved,
  InfoState.Resolved,
  InfoState.Removed,
  InfoState.Optional,
];

const infoProgress = (state: ApplicationState): SectionInfoState => {
  const fields = all(state.student, state.student.config, state.config);
  const { personalInfo, courses, deposit, agentDetails } = fields;

  if (
    personalInfo.some(f => f.state === InfoState.Conflict) ||
    courses.some(c => c.some(f => f.state === InfoState.Conflict)) ||
    deposit.some(f => f.state === InfoState.Conflict) ||
    agentDetails.some(f => f.state === InfoState.Conflict) ||
    hasAgentConflicts(state)
  ) {
    return SectionInfoState.Conflict;
  } else if (
    personalInfo.some(f => f.state === InfoState.Update) ||
    courses.some(c => c.some(f => f.state === InfoState.Update)) ||
    deposit.some(f => f.state === InfoState.Update) ||
    agentDetails.some(f => f.state === InfoState.Update)
  ) {
    return SectionInfoState.Update;
  } else if (
    personalInfo.some(f => f.state === InfoState.Pending) ||
    courses.some(c => c.some(f => f.state === InfoState.Pending)) ||
    deposit.some(f => f.state === InfoState.Pending) ||
    agentDetails.some(f => f.state === InfoState.Pending)
  ) {
    return SectionInfoState.Pending;
  } else if (
    personalInfo.every(f => completedStates.includes(f.state)) &&
    courses.every(c => c.every(f => completedStates.includes(f.state))) &&
    deposit.every(f => [...completedStates, InfoState.Skipped].includes(f.state)) &&
    agentDetails.every(f => completedStates.includes(f.state))
  ) {
    return SectionInfoState.Complete;
  }
  return SectionInfoState.InProgress;
};

const checklistProgress = (state: ApplicationState): number => {
  // Only include agent details in progress when the region-level feature is enabled and intake is post-cutoff
  const agentDetailsActive = state.student.config?.agentDetailsEnabled !== false && selectIntakeAfterCutoff(state);
  const agentDetailsRequired = agentDetailsActive ? (state.student.summary.agentDetailsRequired ?? 0) : 0;
  const agentDetailsComplete = agentDetailsActive ? (state.student.summary.agentDetailsComplete ?? 0) : 0;

  const total =
    state.student.summary.studentDetailsRequired +
    state.student.summary.historyRequired +
    state.student.summary.consentsRequired +
    state.student.summary.financeRequired +
    agentDetailsRequired;
  const complete =
    state.student.summary.studentDetailsComplete +
    state.student.summary.historyComplete +
    state.student.summary.consentsComplete +
    state.student.summary.financeComplete +
    agentDetailsComplete;

  return +((complete / total) * 100).toFixed(0) || 0;
};

const interviewProgress = (state: ApplicationState): number => {
  let total = 4;
  // prettier-ignore
  total -= state.student.config?.Interview?.Page1?.hidden === true ? 1 : 0;
  total -= state.student.config?.Interview?.Page2?.hidden === true ? 1 : 0;
  total -= state.student.config?.Interview?.Page3?.hidden === true ? 1 : 0;
  total -= state.student.config?.Interview?.Page4?.hidden === true ? 1 : 0;

  let complete = 0;
  // prettier-ignore
  complete +=
    state.student.config?.Interview?.Page1?.hidden !== true &&
    state.student?.preCasInterview?.isPage1Complete
      ? 1
      : 0;
  complete +=
    state.student.config?.Interview?.Page2?.hidden !== true && state.student.preCasInterview.isPage2Complete ? 1 : 0;
  complete +=
    state.student.config?.Interview?.Page3?.hidden !== true && state.student.preCasInterview.isPage3Complete ? 1 : 0;
  complete +=
    state.student.config?.Interview?.Page4?.hidden !== true && state.student.preCasInterview.isPage4Complete ? 1 : 0;

  return Math.floor((complete / total) * 100);
};

const visaPrepProgress = (state: ApplicationState): number => {
  const {
    nonHomeCountryApplication,
    hasTravelledToUk,
    hasLeaveToRemain,
    hasBorderRefusal,
    hasOtherTravellers,
    hasTakenEnglishTest,
  } = visaPrepFlags(state.student.personalInfo, state.student.offer, state.student.history);

  let total = 22;
  // prettier-ignore
  total -= !nonHomeCountryApplication ? 1 : 0;
  total -= !hasTravelledToUk ? 1 : 0;
  total -= !hasLeaveToRemain ? 1 : 0;
  total -= !hasBorderRefusal ? 1 : 0;
  total -= !hasOtherTravellers ? 1 : 0;
  total -= hasTakenEnglishTest ? 1 : 0;
  let complete = 0;
  // prettier-ignore
  complete += state.student.visa.isPage1Complete ? 1 : 0;
  complete += state.student.visa.isPage1_2Complete ? 1 : 0;
  complete += nonHomeCountryApplication && state.student.visa.isPage2_1Complete ? 1 : 0;
  complete += state.student.visa.isPage2_2Complete ? 1 : 0;
  complete += hasTravelledToUk && state.student.visa.isPage2_3Complete ? 1 : 0;
  complete += state.student.visa.isPage2_4Complete ? 1 : 0;
  complete += hasLeaveToRemain && state.student.visa.isPage2_5Complete ? 1 : 0;
  complete += hasBorderRefusal && state.student.visa.isPage2_6Complete ? 1 : 0;
  complete += state.student.visa.isPage2_7Complete ? 1 : 0;
  complete += state.student.visa.isPage2_8Complete ? 1 : 0;
  complete += state.student.visa.isPage3_1Complete ? 1 : 0;
  complete += state.student.visa.isPage3_2Complete ? 1 : 0;
  complete += state.student.visa.isPage3_3Complete ? 1 : 0;
  complete += state.student.visa.isPage3_4Complete ? 1 : 0;
  complete += state.student.visa.isPage3_5Complete ? 1 : 0;
  complete += hasOtherTravellers && state.student.visa.isPage4Complete ? 1 : 0;
  complete += state.student.visa.isPage5Complete ? 1 : 0;
  complete += state.student.visa.isPage6Complete ? 1 : 0;
  complete += state.student.visa.isPage7_1Complete ? 1 : 0;
  complete += !hasTakenEnglishTest && state.student.visa.isPage7_2Complete ? 1 : 0;
  complete += state.student.visa.isPage7_3Complete ? 1 : 0;
  complete += state.student.visa.isPage8Complete ? 1 : 0;

  return Math.floor((complete / total) * 100);
};

export const hasScheduledCalendlyInterviews = (calendlyInterviews: ICalendlyEvent[]): boolean => {
  return (
    calendlyInterviews.length !== 0 &&
    calendlyInterviews.every((interview: ICalendlyEvent) => {
      return Object.entries(interview.scheduledEvents ?? {}).length !== 0;
    })
  );
};

export const hasScheduledRecordedInterviews = (recordedInterviewStatus: RecordedInterviewSystemStatus): boolean => {
  return recordedInterviewStatus !== RecordedInterviewSystemStatus.NONE;
};

interface CountResp {
  docsTotal: number;
  docsUploaded: number;
}
function getDocsCount(summary: ISummary, role: UserRole): CountResp {
  const {
    docsTotal = 0,
    docsUploaded = 0,
    agentDocsTotal = 0,
    agentDocsUploaded = 0,
    staffDocsTotal = 0,
    staffDocsUploaded = 0,
  } = summary;
  const isAgentUser = isAgentOrAgencyRole(role);
  const isStaffUser = isUniStaffRole(role);
  const isEnroly = isEnrolyStaff(role);

  if (isAgentUser) {
    return { docsTotal: agentDocsTotal + docsTotal, docsUploaded: agentDocsUploaded + docsUploaded };
  } else if (isStaffUser || isEnroly) {
    // staff and enroly has access of agentOnly + staffOnly + other docsType access
    return {
      docsTotal: agentDocsTotal + staffDocsTotal + docsTotal,
      docsUploaded: agentDocsUploaded + staffDocsUploaded + docsUploaded,
    };
  }
  return { docsTotal, docsUploaded };
}

function getFinDocsCount(summary: ISummary, role: UserRole): CountResp {
  const {
    finDocsTotal = 0,
    finDocsUploaded = 0,
    agentFinDocsTotal = 0,
    agentFinDocsUploaded = 0,
    staffFinDocsTotal = 0,
    staffFinDocsUploaded = 0,
  } = summary;
  const isAgentUser = isAgentOrAgencyRole(role);
  const isStaffUser = isUniStaffRole(role);
  const isEnroly = isEnrolyStaff(role);

  if (isAgentUser) {
    return { docsTotal: agentFinDocsTotal + finDocsTotal, docsUploaded: agentFinDocsUploaded + finDocsUploaded };
  } else if (isStaffUser || isEnroly) {
    // staff and enroly user has access of agentOnly + staffOnly + other docsType access
    return {
      docsTotal: agentFinDocsTotal + staffFinDocsTotal + finDocsTotal,
      docsUploaded: agentFinDocsUploaded + staffFinDocsUploaded + finDocsUploaded,
    };
  }
  return { docsTotal: finDocsTotal, docsUploaded: finDocsUploaded };
}

function getFollowUpDocsCount(
  summary: ISummary,
  role: UserRole,
  isInUK: boolean,
): { beforeTravelDoc: CountResp; afterTravelDoc: CountResp } {
  const {
    beforeTravelDocsComplete = 0,
    beforeTravelDocsRequired = 0,
    afterArrivalDocsComplete = 0,
    afterArrivalDocsRequired = 0,
    agentBeforeTravelDocsComplete = 0,
    agentBeforeTravelDocsRequired = 0,
    agentAfterArrivalDocsComplete = 0,
    agentAfterArrivalDocsRequired = 0,
    staffBeforeTravelDocsComplete = 0,
    staffBeforeTravelDocsRequired = 0,
    staffAfterArrivalDocsComplete = 0,
    staffAfterArrivalDocsRequired = 0,
  } = summary;
  const isAgentUser = isAgentOrAgencyRole(role);
  const isStaffUser = isUniStaffRole(role);
  const isEnroly = isEnrolyStaff(role);

  const beforeTravelDoc: CountResp = {
    docsTotal: 0,
    docsUploaded: 0,
  };
  const afterTravelDoc: CountResp = {
    docsTotal: 0,
    docsUploaded: 0,
  };

  if (isAgentUser) {
    beforeTravelDoc['docsTotal'] = agentBeforeTravelDocsRequired + beforeTravelDocsRequired;
    beforeTravelDoc['docsUploaded'] = agentBeforeTravelDocsComplete + beforeTravelDocsComplete;
    afterTravelDoc['docsTotal'] = isInUK ? agentAfterArrivalDocsRequired + afterArrivalDocsRequired : 0;
    afterTravelDoc['docsUploaded'] = isInUK ? agentAfterArrivalDocsComplete + afterArrivalDocsComplete : 0;
  } else if (isStaffUser || isEnroly) {
    // staff and enroly has access of agentOnly + staffOnly + other docsType access
    beforeTravelDoc['docsTotal'] =
      agentBeforeTravelDocsRequired + staffBeforeTravelDocsRequired + beforeTravelDocsRequired;
    beforeTravelDoc['docsUploaded'] =
      agentBeforeTravelDocsComplete + staffBeforeTravelDocsComplete + beforeTravelDocsComplete;
    afterTravelDoc['docsTotal'] = isInUK
      ? agentAfterArrivalDocsRequired + staffAfterArrivalDocsRequired + afterArrivalDocsRequired
      : 0;
    afterTravelDoc['docsUploaded'] = isInUK
      ? agentAfterArrivalDocsComplete + staffAfterArrivalDocsComplete + afterArrivalDocsComplete
      : 0;
  } else {
    beforeTravelDoc['docsTotal'] = beforeTravelDocsRequired;
    beforeTravelDoc['docsUploaded'] = beforeTravelDocsComplete;
    afterTravelDoc['docsTotal'] = isInUK ? afterArrivalDocsRequired : 0;
    afterTravelDoc['docsUploaded'] = isInUK ? afterArrivalDocsComplete : 0;
  }
  return { beforeTravelDoc, afterTravelDoc };
}

function mapStateToProps(state: ApplicationState): IStateProps {
  const travelStatusSection = state.student.enrolmentStatus?.travelStatusSection;
  const isInUK =
    travelStatusSection?.status === TravelStatusEnum.AlreadyInUk ||
    (travelStatusSection?.status === TravelStatusEnum.Arrived &&
      travelStatusSection.arrivalDate &&
      isAfter(now(), travelStatusSection.arrivalDate));

  const infoState = infoProgress(state);
  const checklistComplete = checklistProgress(state);

  const { docsTotal: docsRequired, docsUploaded: docsComplete } = getDocsCount(state.student.summary, state.auth.role);
  const { docsTotal: finDocsRequired, docsUploaded: finDocsComplete } = getFinDocsCount(
    state.student.summary,
    state.auth.role,
  );
  const { beforeTravelDoc, afterTravelDoc } = getFollowUpDocsCount(state.student.summary, state.auth.role, isInUK);

  const offerStatus = state.student.offer.status;
  const hasRelevantOfferStatus =
    offerStatus !== undefined &&
    [CoreStatusExtended.CAS_ISSUED, CoreStatusExtended.VISA_ISSUED, CoreStatusExtended.VISA_REFUSED].includes(
      offerStatus,
    );

  const isPreDepartureDocsHidden =
    state.student.config?.preDepartureDocSectionStatus === SectionStatusType.INACTIVE ||
    !(hasRelevantOfferStatus || state.student.summary.isBeforeTravelDocsSectionStarted);

  const isAfterTravelDocsHidden =
    state.student.config?.afterTravelDocSectionStatus === SectionStatusType.INACTIVE ||
    !(hasRelevantOfferStatus || state.student.summary.isAfterArrivalDocsSectionStarted);

  const interviewComplete = interviewProgress(state);
  const visaPrepComplete = visaPrepProgress(state);

  const isPreCasFormHidden = state.student.config?.interviewSectionStatus === SectionStatusType.INACTIVE;
  const isGeneralDocFormHidden = state.student.config?.docSectionStatus === SectionStatusType.INACTIVE;
  const isFinanceDocFormHidden = state.student.config?.finDocSectionStatus === SectionStatusType.INACTIVE;

  const isPathway = !!state.auth.isPathway;
  const isPathwayStudent = !!state.student.core.pathway;

  const calendlyInterviews = state.student.calendlyEvents ?? [];
  const recordedInterviewSystemStatus =
    state.student.offer.recordedInterviewSystemStatus ?? RecordedInterviewSystemStatus.NONE;

  const displayScheduledCalendlyInterviewsAllowed =
    state.data.isConfigLoaded &&
    state.data.isStudentLoaded &&
    getDisplayCalendlyInterviewsAllowed(
      state.auth.role,
      isPathway,
      isPathwayStudent,
      state.config.relatedPathwaySettings,
    );
  let isScheduledInterviewsHidden = true;
  if (displayScheduledCalendlyInterviewsAllowed) {
    isScheduledInterviewsHidden =
      state.student.config?.scheduledInterviewsSectionStatus === SectionStatusType.INACTIVE &&
      !hasScheduledCalendlyInterviews(calendlyInterviews);
  }

  const displayRecordedInterviewsAllowed =
    state.data.isConfigLoaded &&
    state.data.isStudentLoaded &&
    getDisplayRecordedInterviewAllowed(
      state.auth.role,
      isPathway,
      isPathwayStudent,
      state.config.relatedPathwaySettings,
    );

  let isRecordedInterviewHidden = true;
  if (displayRecordedInterviewsAllowed) {
    isRecordedInterviewHidden =
      displayRecordedInterviewsAllowed &&
      state.student.config?.recordedInterviewSectionStatus === SectionStatusType.INACTIVE &&
      !hasScheduledRecordedInterviews(recordedInterviewSystemStatus);
  }

  const successPercentage =
    state.student.summary.riskRating === 0 || state.student.summary.riskRating === undefined
      ? undefined
      : state.student.summary.riskRating;

  const trackerVisible = isTrackerVisible(state.student.summary) && hasPerm(state.auth.role, P.VIEW_CAS_TRACKER);

  return {
    lockAgent: state.student.offer.lockAgent ?? false,
    student: state.student,
    config: state.config,
    auth: state.auth,
    isLoading: state.ui.isLoading,
    isSocketUp: state.ui.isSocketUp,
    sections: state.ui.sections,
    infoState,
    checklistComplete,

    // Note: agent/staff docs counts are for display in the UI only.
    // docsComplete, finDocsComplete, beforeFollowUpDocsComplete, afterFollowUpDocsComplete - will contains count for other then agent/staff only docs type (e.g. infoOnly, required, ...)
    docsComplete,
    docsRequired,
    finDocsComplete,
    finDocsRequired,
    beforeFollowUpDocsComplete: beforeTravelDoc.docsUploaded,
    beforeFollowUpDocsRequired: beforeTravelDoc.docsTotal,
    afterFollowUpDocsComplete: afterTravelDoc.docsUploaded,
    afterFollowUpDocsRequired: afterTravelDoc.docsTotal,

    interviewComplete,
    visaPrepComplete,
    isChecklistRisk: isChecklistRisk(state.student.risks, state.auth.role),
    isInactiveChecklistRisk: isInactiveChecklistRisk(state.student.risks, state.auth.role),
    isConfidenceScoreView: hasPerm(state.auth.role, P.CONFIDENCE_SCORE_VIEW),
    isDocsDownloadsActive:
      state.config?.documentDownloadsActive &&
      hasPerm(state.auth.role, P.ALL_DOCUMENTS_DOWNLOAD, state.auth.serviceProviderType) &&
      !!docsComplete,
    isDocsRisk: isDocsRisk(state.student.risks, state.auth.role),
    isInactiveDocRisk: isInactiveDocsRisk(state.student.risks, state.auth.role),
    isFinDocsRisk: isFinDocRisk(state.student, state.auth.role),
    isInactiveFinDocRisk: isInactiveFinDocsRisk(state.student, state.auth.role),
    isPreCasRisk: isPreCasRisk(state.student.risks, state.auth.role),
    isInactivePreCasRisk: isInactivePreCasRisk(state.student.risks, state.auth.role),
    isUnlockVisaPrep: state.student.offer.visaSupport === true && hasPerm(state.auth.role, P.VISA_PREP_UNLOCK),
    preCasFlags: preCasFlags(state.student.preCasInterview, state.auth.role),
    userName: state.auth.student_number ?? '',
    displayName: getDisplayName(state.auth, state.config),
    successPercentage: state.student.offer.isPISectionComplete ? successPercentage : undefined,
    riskList: state.student.risks,
    openedStudentRecords: state.dashboard.openedStudentRecords,
    hasVisaPrepBeenActivated: state.student.visa.hasBeenActivated,
    isStudentLoaded: state.data.isStudentLoaded,
    userRole: state.auth.role,
    groups: state.auth.groups,
    visaAssistance: state.student.offer.visaSupport || false,
    isAgent: !!state.student?.summary?.agentAccess,
    showLastTouch: hasPerm(state.auth.role, P.LAST_TOUCH_VIEW),
    showArrivalStatus: state.student.offer.status === CoreStatusExtended.VISA_ISSUED,
    travelStatusSection: state.student.enrolmentStatus.travelStatusSection || {},
    showAgent: hasPerm(state.auth.role, P.AGENT_VIEW),
    hasAgentMessages: filterMessagesByType(state.student.messages, 'agent').length > 0,
    isPreDepartureDocsHidden,
    isAfterTravelDocsHidden,
    isPreCasFormHidden,
    isGeneralDocFormHidden,
    isFinanceDocFormHidden,
    isScheduledInterviewsHidden,
    isRecordedInterviewHidden,
    isTrackerVisible: trackerVisible,
    isPathway: state.auth.isPathway ?? false,
    allTags: state.config.tags || [], // using 'tags' if manage tags was opened in config (create/delete/edit)
    allProgrammableTags: state.programmableTags.programmableTags,
    calendlyInterviewsInfoStatus: state.student.offer.calendlyInterviewsInfoStatus || EventInfoStatus.NONE,
    recordedInterviewSystemStatus:
      state.student.offer.recordedInterviewSystemStatus || RecordedInterviewSystemStatus.NONE,
    processedByUni: state.config.processedByUni ?? false,
    allowedAliases: ((): string[] => {
      const isAdminOrEnroly = state.auth.role === UserRole.Admin || state.auth.role === UserRole.Enroly;
      return isAdminOrEnroly ? state.config.admAllowedAliases || [] : state.config.allowedAliases || [];
    })(),
    currentUser: state.auth.currentUser,
  };
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  getStudent: (id: string, university: string, intakeId: string, offerId: string): void => {
    dispatch(getStudentRecord(id, university, intakeId, offerId));
  },
  getAuditLog: (id: string, university: string, intakeId: string, offerId: string): void => {
    dispatch(getAuditLog({ studentNo: id, uni: university, intakeId, offerId }));
  },
  onUpdateStatus: (id: string, status: Status) => dispatch(updateStatus(id, status)),
  onSubmitMessage: (message: IMessage) => dispatch(saveMessage(message)),
  onOverrideMaturedDate: (_date: IDate): void => {
    // TODO
    // dispatch(createUpdateFinanceFieldAction(Q.OVERRIDE_MATURED_DATE, date));
  },
  onOverrideVisaStatus: (status: VisaStatus) => dispatch(updateVisaStatus(status)),
  onOverrideArrivalStatus: (arrivalStatus: IArrivalStatus): void => {
    dispatch(updateArrivalStatus(arrivalStatus));
  },
  markMessagesRead: (messageIds: Array<string>) => dispatch(markMessagesRead(messageIds)),
  markMessageUnread: (messageId: string) => dispatch(markMessageUnread(messageId)),
  // onLogout: () => dispatch(logout()),
  onDownload: (includeHidden?: boolean) => dispatch(downloadAttachments({ includeHidden })),
  unpinTagsFromStudents: ({
    tags,
    studentNumbers,
  }: {
    tags: { id: string; isProgrammable: boolean }[];
    studentNumbers: string[];
  }) => dispatch(studentsUnpinTags(studentNumbers, tags, true)),
  pinTagsToStudents: ({
    tags,
    studentNumbers,
  }: {
    tags: { id: string; isProgrammable: boolean }[];
    studentNumbers: string[];
  }) => dispatch(studentsPinTags(studentNumbers, tags, true)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DashboardPage);
